import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react'

import { useOpenableControls } from '../../generic-hooks'
import { CartIntermediateDrawer } from '../components/CartIntermediateDrawer'
import { CartIntermediateContext } from '../context/CartIntermediateContext'
import {
  type CartIntermediateContextValue,
  type CartIntermediateDrawerMessages,
  type CartIntermediateEntryObject,
} from '../model/CartIntermediateContextValue'

type CartIntermediateContextProviderProps = {
  children: ReactNode
}

export const CartIntermediateContextProvider: FC<
  CartIntermediateContextProviderProps
> = ({ children }) => {
  const [quantity, setQuantity] = useState<number>(0)
  const [entries, setEntries] = useState<CartIntermediateEntryObject[] | null>(
    null,
  )

  const [drawerMessages, setDrawerMessages] =
    useState<CartIntermediateDrawerMessages>({
      infoMessage: null,
      titleMessage: null,
    })

  const cartIntermediateOpenableControls = useOpenableControls()
  const specialOffersCollapsibleControls = useOpenableControls()

  const openCartIntermediateDrawerWithData = useCallback(
    (
      products: CartIntermediateEntryObject[],
      messages: CartIntermediateDrawerMessages,
      entryQuantity = 1,
    ): void => {
      setEntries(products)
      setQuantity(entryQuantity)
      setDrawerMessages(messages)
      cartIntermediateOpenableControls.handleOpen()
    },
    [
      setEntries,
      setQuantity,
      setDrawerMessages,
      cartIntermediateOpenableControls,
    ],
  )
  const resetCartIntermediateContext = (): void => {
    setQuantity(0)
    setEntries(null)
    setDrawerMessages({
      infoMessage: null,
      titleMessage: null,
    })
  }

  const contextValue = useMemo(
    (): CartIntermediateContextValue => ({
      cartIntermediateOpenableControls,
      drawerMessages,
      entries,
      openCartIntermediateDrawerWithData,
      quantity,
      resetCartIntermediateContext,
      setDrawerMessages,
      setEntries,
      setQuantity,
      specialOffersCollapsibleControls,
    }),
    [
      entries,
      drawerMessages,
      quantity,
      cartIntermediateOpenableControls,
      openCartIntermediateDrawerWithData,
      specialOffersCollapsibleControls,
    ],
  )

  return (
    <CartIntermediateContext.Provider value={contextValue}>
      {children}
      <CartIntermediateDrawer />
    </CartIntermediateContext.Provider>
  )
}
